"use client";

import dynamic from "next/dynamic";

import Skeleton from "@/components/global/SkeletonComponent/server/SkeletonComponent";
import { convertToKebabCase } from "@/utils/common/convertHelper/convertToKebabCase";

import SkeletonKlubCardComponent from "../../klub/components/KlubLifeStyleLayoutComponent/SkeletonKlubCardComponent";
import CrossSellSkeletonComponent from "../CrossSellLayoutComponent/CrossSellSkeletonComponent";
import SkeletonEntryKlubIntroComponent from "../EntryKlubSlideLayoutComponent/SkeletonEntryKlubIntroComponent";
import SkeletonPromotionSlideComponent from "../slide/Skeleton/SkeletonPromotionSlideComponent";

const PromotionSlideComponent = dynamic(
  () => import("../slide/PromotionSlideComponent"),
  {
    ssr: false,
    loading: () => (
      <SkeletonPromotionSlideComponent isHomePage showTitleSkeleton={false} />
    ),
  },
);

const EntryKlubIntroComponent = dynamic(
  () => import("../EntryKlubSlideLayoutComponent/EntryKlubIntroComponent"),
  {
    ssr: false,
    loading: () => <SkeletonEntryKlubIntroComponent />,
  },
);

const EntryKlubSlideLayoutComponent = dynamic(
  () =>
    import("../EntryKlubSlideLayoutComponent/EntryKlubSlideLayoutComponent"),
  {
    ssr: false,
    loading: () => (
      <div className="z-20 mx-auto max-w-7xl overflow-hidden pl-4 md:pl-0">
        <SkeletonKlubCardComponent />
      </div>
    ),
  },
);

const CrossSellLayoutComponent = dynamic(
  () => import("../CrossSellLayoutComponent/CrossSellLayoutComponent"),
  {
    ssr: false,
    loading: () => (
      <div className="bg-background-baigeBG">
        <CrossSellSkeletonComponent />
      </div>
    ),
  },
);

const PopularDestinationSlideComponent = dynamic(
  () => import("../slide/PopularDestinationSlideComponent"),
  {
    ssr: false,
    loading: () => <Skeleton className="h-[26.875rem] w-full md:h-[75rem]" />,
  },
);

const TravelStoriesContentComponent = dynamic(
  () => import("../TravelStoriesContentComponent"),
  {
    ssr: false,
    loading: () => (
      <div className="py-6">
        <Skeleton className="h-[50rem] w-full md:h-[75rem]" />
      </div>
    ),
  },
);

const BannerGotherApplicationAndHomePathAllComponent = dynamic(
  () =>
    import(
      "@/components/global/BannerGotherApplication/BannerGotherApplication"
    ),
  {
    ssr: false,
    loading: () => <div />,
  },
);

const HomeWhyWithGotherComponent = dynamic(
  () =>
    import(
      "@/components/global/WhyWithGotherComponent/HomeWhyWithGotherComponent/HomeWhyWithGotherComponent"
    ),
  {
    ssr: false,
    loading: () => <div />,
  },
);

const HomeLandingGroupComponent = ({
  lang,
  isDesktop,
}: {
  lang: string;
  isDesktop: boolean;
}) => {
  return (
    <div>
      <section
        id={convertToKebabCase("PromotionSlide")}
        className="section-promotion-homepage overflow-hidden"
      >
        <div className="mx-auto md:my-0 md:px-[16px] xl:max-w-7xl xl:px-0">
          <PromotionSlideComponent isHomePage showTitleSkeleton={false} />
        </div>
      </section>

      <section
        id={convertToKebabCase("section-entry-klub-component")}
        className="klub-home z-[1] flex min-h-[521px] w-full flex-col bg-[#BC2C1C] pb-[16px] md:min-h-[540px] md:pb-[40px] lg:min-h-[574px] xl:min-h-[613px]"
      >
        <EntryKlubIntroComponent />
        <EntryKlubSlideLayoutComponent />

        <div className="background absolute bottom-0 left-0 z-0 h-[210px] w-full bg-left-top bg-no-repeat md:h-[350px] md:bg-cover lg:h-[421px]">
          <div className="flex size-full justify-end">
            <div className="curve-left w-full bg-[#BC2C1C] md:w-1/2" />
            <div className="hidden bg-[#BC2C1C] md:inline-flex md:w-1/2" />
          </div>
          <span className="absolute right-0 top-0 mt-[-50px] w-[64px] md:mt-[-80px] md:block md:w-[100px] lg:mt-[-113px] lg:w-[140px] xl:mt-[-174px] xl:w-[216px]">
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 216 176"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M222 176H0C156.36 176 222 59.2005 222 0V176Z"
                fill="#BC2C1C"
              />
            </svg>
          </span>
        </div>
      </section>
      {/* height is for reserve space for content and rootMargin is for trigger preload */}
      {/* <LazyLoad height={500} rootMargin="300px 0px"> */}
      <section>
        <CrossSellLayoutComponent lang={lang} />
      </section>

      <div
        id={convertToKebabCase("sectionTravelStories")}
        className="relative h-auto w-full bg-[url('https://d31jk6ljx7wihh.cloudfront.net/images/bg-pattern-heart@2x.webp')] bg-[length:263px_auto] bg-left-top bg-no-repeat pl-4 md:bg-[length:379px_auto] md:px-0"
      >
        <TravelStoriesContentComponent lang={lang} />
      </div>
      <section
        id={convertToKebabCase("PopularDestinationSlide")}
        className="mx-auto flex flex-col gap-y-10 px-[16px] xl:max-w-7xl xl:px-0"
      >
        {/* <HeadBannerComponent {...HeaderBannerProps} /> */}
        <div className="my-6 md:mt-10 xl:mb-10">
          <PopularDestinationSlideComponent />
        </div>

        {/* <PopularHotelSlideComponent /> */}
        {/* <ActivityInterestingSlideComponent /> */}
      </section>
      <HomeWhyWithGotherComponent />
      <BannerGotherApplicationAndHomePathAllComponent
        lang={lang}
        isDesktop={isDesktop}
      />
      {/* </LazyLoad> */}
    </div>
  );
};

export default HomeLandingGroupComponent;
